/**
 * DimensionCache - LRU cache for image/video dimensions and metadata
 *
 * This cache stores dimension information to avoid redundant network requests
 * and DOM element creation for dimension extraction.
 *
 * Features:
 * - LRU eviction when max size is reached
 * - TTL expiration (default 1 hour)
 * - Support for dimensions, audio detection, and other metadata
 */

class DimensionCache {
  constructor(maxSize = 500, ttl = 3600000) {
    // 1 hour TTL
    this.maxSize = maxSize;
    this.ttl = ttl;
    this.cache = new Map();
  }

  /**
   * Generate cache key for dimensions
   */
  getDimensionKey(src) {
    return `${src}_dimensions`;
  }

  /**
   * Generate cache key for audio detection
   */
  getAudioKey(src) {
    return `${src}_hasAudio`;
  }

  /**
   * Get cached value by key
   */
  get(key) {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Move to end (mark as recently used)
    this.cache.delete(key);
    this.cache.set(key, entry);

    return entry.value;
  }

  /**
   * Set value in cache
   */
  set(key, value) {
    // If key exists, delete it first to update position
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    // If cache is full, delete oldest entry (first in Map)
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
  }

  /**
   * Check if key exists and is not expired
   * Note: This method does NOT update the LRU order
   */
  has(key) {
    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    // Check if entry has expired
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Get cache size
   */
  size() {
    return this.cache.size;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      ttl: this.ttl
    };
  }

  /**
   * Helper: Get cached dimensions for a source
   */
  getDimensions(src) {
    return this.get(this.getDimensionKey(src));
  }

  /**
   * Helper: Set cached dimensions for a source
   */
  setDimensions(src, dimensions) {
    this.set(this.getDimensionKey(src), dimensions);
  }

  /**
   * Helper: Get cached audio detection result
   */
  getHasAudio(src) {
    return this.get(this.getAudioKey(src));
  }

  /**
   * Helper: Set cached audio detection result
   */
  setHasAudio(src, hasAudio) {
    this.set(this.getAudioKey(src), hasAudio);
  }
}

// Export singleton instance
export const dimensionCache = new DimensionCache();

// Export class for testing
export default DimensionCache;
